import { Col, DatePicker, Form, FormInstance, Row, Select } from 'antd';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import CustomButton from 'components/custom-button';
import DebounceSelect from 'components/debounce-select/select';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';

export enum OVERTIME_REQUEST_STATUS {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  CANCELLED = 'CANCELLED',
}

type RangeValue = [Dayjs | null, Dayjs | null] | null;
type UserOption = { value: number; label: string };

export interface OvertimeFilterFormValue {
  status?: OVERTIME_REQUEST_STATUS;
  range: RangeValue;
  userId?: UserOption;
  assignedApproverId?: UserOption;
  hasAssignedApprover?: boolean;
}

interface FilterSubmitValue {
  status?: OVERTIME_REQUEST_STATUS;
  from?: string;
  to?: string;
  userId?: number;
  assignedApproverId?: number;
  hasAssignedApprover?: boolean;
}

interface FilterProps {
  onSubmit: (value: FilterSubmitValue) => void;
  /** Form do trang giữ, để trang đặt/xoá ô Nhân viên khi URL đổi (?userId=). */
  form?: FormInstance<OvertimeFilterFormValue>;
  /** Giá trị ban đầu lấy từ URL; nút "Xoá lọc" vẫn về trống như cũ. */
  preset?: Partial<OvertimeFilterFormValue>;
}

interface Props extends FilterProps {}

const { Option } = Select;
const OVERTIME_FILTER_SELECT_CLASS = 'overtime-filter-select';

const initialValues: OvertimeFilterFormValue = {
  status: undefined,
  range: null,
  userId: undefined,
  assignedApproverId: undefined,
  hasAssignedApprover: undefined,
};

const DEFAULT_SEARCH_LIMIT = 20;

const buildUserLabel = (user: any): string => {
  const full = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
  const head = full || user?.nickName || user?.username || `User #${user?.id}`;
  const username = user?.username && user.username !== head ? ` (${user.username})` : '';
  return user?.email ? `${head}${username} — ${user.email}` : `${head}${username}`;
};

const fetchUsers = async (search: string) => {
  const query = search.trim();
  if (query.length < 2) return [];
  const res: any = await AxiosUtils.get(API.searchUserAdmin, {
    query,
    limit: DEFAULT_SEARCH_LIMIT,
  });
  const list: any[] = Array.isArray(res?.data) ? res.data : res?.data?.data || [];
  return list.map((user) => ({ value: user.id, label: buildUserLabel(user) }));
};

export const OVERTIME_STATUS_LABEL: Record<OVERTIME_REQUEST_STATUS, string> = {
  [OVERTIME_REQUEST_STATUS.PENDING]: 'Chờ duyệt',
  [OVERTIME_REQUEST_STATUS.APPROVED]: 'Đã duyệt',
  [OVERTIME_REQUEST_STATUS.REJECTED]: 'Từ chối',
  [OVERTIME_REQUEST_STATUS.CANCELLED]: 'Đã huỷ',
};

const rangePresets = [
  { label: 'Hôm nay', value: [dayjs(), dayjs()] as [Dayjs, Dayjs] },
  { label: '7 ngày', value: [dayjs().subtract(6, 'day'), dayjs()] as [Dayjs, Dayjs] },
  { label: '30 ngày', value: [dayjs().subtract(29, 'day'), dayjs()] as [Dayjs, Dayjs] },
  {
    label: 'Tháng này',
    value: [dayjs().startOf('month'), dayjs().endOf('month')] as [Dayjs, Dayjs],
  },
];

const Filter: React.FC<Props> = ({ onSubmit, form: formProp, preset }) => {
  const [ownForm] = Form.useForm<OvertimeFilterFormValue>();
  const form = formProp || ownForm;

  const submitValue = (value: OvertimeFilterFormValue) => {
    const from = value.range?.[0] ? dayjs(value.range[0]).startOf('day').toISOString() : undefined;
    const to = value.range?.[1] ? dayjs(value.range[1]).endOf('day').toISOString() : undefined;
    onSubmit({
      status: value.status,
      from,
      to,
      userId: value.userId?.value,
      assignedApproverId: value.assignedApproverId?.value,
      hasAssignedApprover: value.hasAssignedApprover,
    });
  };

  const onClearFilter = () => {
    form.setFieldsValue(initialValues as any);
    submitValue(initialValues);
  };

  return (
    <Form
      form={form}
      name="overtime-filter"
      layout="vertical"
      onFinish={submitValue}
      initialValues={{ ...initialValues, ...preset }}
      className="custom-form overtime-filter-form"
    >
      <Row gutter={[16, 8]}>
        <Col xl={6} lg={8} md={12} xs={24}>
          <Form.Item name="status" label="Trạng thái">
            <Select
              allowClear
              placeholder="Tất cả"
              size="small"
              className={OVERTIME_FILTER_SELECT_CLASS}
              style={{ width: '100%' }}
            >
              <Option value={OVERTIME_REQUEST_STATUS.PENDING}>
                {OVERTIME_STATUS_LABEL[OVERTIME_REQUEST_STATUS.PENDING]}
              </Option>
              <Option value={OVERTIME_REQUEST_STATUS.APPROVED}>
                {OVERTIME_STATUS_LABEL[OVERTIME_REQUEST_STATUS.APPROVED]}
              </Option>
              <Option value={OVERTIME_REQUEST_STATUS.REJECTED}>
                {OVERTIME_STATUS_LABEL[OVERTIME_REQUEST_STATUS.REJECTED]}
              </Option>
              <Option value={OVERTIME_REQUEST_STATUS.CANCELLED}>
                {OVERTIME_STATUS_LABEL[OVERTIME_REQUEST_STATUS.CANCELLED]}
              </Option>
            </Select>
          </Form.Item>
        </Col>
        <Col xl={6} lg={8} md={12} xs={24}>
          <Form.Item name="range" label="Khoảng thời gian">
            <DatePicker.RangePicker
              allowClear
              size="small"
              format="YYYY-MM-DD"
              style={{ width: '100%' }}
              placeholder={['Từ', 'Đến']}
              presets={rangePresets}
            />
          </Form.Item>
        </Col>
        <Col xl={6} lg={8} md={12} xs={24}>
          <Form.Item name="userId" label="Nhân viên">
            <DebounceSelect
              size="small"
              placeholder="Nhập tên / username / email"
              fetchOptions={fetchUsers}
              className={OVERTIME_FILTER_SELECT_CLASS}
              style={{ width: '100%' }}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col xl={6} lg={8} md={12} xs={24}>
          <Form.Item name="assignedApproverId" label="Người duyệt">
            <DebounceSelect
              size="small"
              placeholder="Nhập tên / username / email"
              fetchOptions={fetchUsers}
              className={OVERTIME_FILTER_SELECT_CLASS}
              style={{ width: '100%' }}
              allowClear
            />
          </Form.Item>
        </Col>
        <Col xl={6} lg={8} md={12} xs={24}>
          <Form.Item name="hasAssignedApprover" label="Đã gán người duyệt">
            <Select
              allowClear
              placeholder="Tất cả"
              size="small"
              className={OVERTIME_FILTER_SELECT_CLASS}
              style={{ width: '100%' }}
            >
              <Option value={true}>Có</Option>
              <Option value={false}>Không</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <CustomButton
              bgColor="secondary-green"
              htmlType="submit"
              text="Tìm kiếm"
              icon={<SearchOutlined />}
            />
            <CustomButton
              text="Xoá lọc"
              bgColor="primary-grey"
              icon={<CloseOutlined />}
              onClick={onClearFilter}
            />
          </div>
        </Col>
      </Row>
    </Form>
  );
};

export default Filter;
